import React, { useMemo, useState } from "react";
import clsx from "clsx";

import { EmptyState, Text, View } from "../base";
import { Icon } from "../base/Icon";
import { IconButton } from "../base/IconButton";
import { formatCount } from "../../utils/adminFormat";

export interface AdminColumn<T> {
  key: string;
  header: string;
  width?: string;
  align?: "left" | "right";
  render: (row: T) => React.ReactNode;
  sortValue?: (row: T) => string | number | null;
}

export interface AdminTableProps<T> {
  columns: AdminColumn<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  selectedKey?: string | null;
  isLoading?: boolean;
  emptyState?: { icon: string; title: string; description?: string };
  defaultSort?: { key: string; dir: "asc" | "desc" };
  /** Controlled sorting: the SERVER owns row order (sort/sortDir query
   *  params) and this table only renders it. Required whenever rows are a
   *  server-side page of a larger set — client-sorting one page silently
   *  masquerades as a global sort. Columns stay clickable via `sortValue`
   *  presence (the fn itself is unused in this mode). */
  serverSort?: { sort: { key: string; dir: "asc" | "desc" } | null; onChange: (sort: { key: string; dir: "asc" | "desc" }) => void };
  pagination?: { page: number; pageSize: number; total: number; onPageChange: (p: number) => void };
}

const SKELETON_ROWS = 8;

export function AdminTable<T>({ columns, rows, rowKey, onRowClick, selectedKey, isLoading, emptyState, defaultSort, serverSort, pagination }: AdminTableProps<T>) {
  const [localSort, setLocalSort] = useState<{ key: string; dir: "asc" | "desc" } | null>(defaultSort ?? null);
  const sort = serverSort ? serverSort.sort : localSort;

  const gridTemplateColumns = columns.map((column) => column.width ?? "1fr").join(" ");
  // px columns at face value, fr columns at a ~120px floor, plus px-4 horizontal padding.
  const minWidth = columns.reduce((sum, column) => sum + (column.width?.endsWith("px") ? parseInt(column.width) : 120), 32);

  const sortedRows = useMemo(() => {
    if (serverSort) return rows; // server already ordered the page
    if (!sort) return rows;
    const column = columns.find((c) => c.key === sort.key);
    const sortValue = column?.sortValue;
    if (!sortValue) return rows;
    return [...rows].sort((a, b) => {
      const av = sortValue(a);
      const bv = sortValue(b);
      // nulls always sink to the bottom regardless of direction
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      const cmp = typeof av === "number" && typeof bv === "number" ? av - bv : String(av).localeCompare(String(bv));
      return sort.dir === "asc" ? cmp : -cmp;
    });
  }, [rows, sort, columns, serverSort]);

  const onHeaderClick = (column: AdminColumn<T>) => {
    const next = (prev: { key: string; dir: "asc" | "desc" } | null): { key: string; dir: "asc" | "desc" } =>
      prev?.key === column.key ? { key: column.key, dir: prev.dir === "asc" ? "desc" : "asc" } : { key: column.key, dir: "desc" };
    if (serverSort) {
      serverSort.onChange(next(serverSort.sort));
      return;
    }
    setLocalSort(next);
  };

  const isEmpty = !isLoading && rows.length === 0;

  return (
    <View className="flex flex-col overflow-hidden rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-slate-900">
      <div className="overflow-x-auto">
        <div style={{ minWidth }}>
          <div
            className="grid border-b border-gray-200 dark:border-gray-700 px-4"
            style={{ gridTemplateColumns }}
          >
            {columns.map((column) => {
              const isSorted = sort?.key === column.key;
              if (!column.sortValue) {
                return (
                  <span
                    key={column.key}
                    className={clsx(
                      "py-3 pr-2 text-xs font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500 truncate",
                      column.align === "right" && "text-right",
                    )}
                  >
                    {column.header}
                  </span>
                );
              }
              return (
                // Sort header — allowed raw-button exception per src/CLAUDE.md (no DS equivalent)
                <button
                  key={column.key}
                  type="button"
                  onClick={() => onHeaderClick(column)}
                  className={clsx(
                    "flex min-w-0 items-center gap-0.5 py-3 pr-2 text-xs font-medium uppercase tracking-wider transition-colors",
                    isSorted ? "text-gray-600 dark:text-gray-300" : "text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300",
                    column.align === "right" && "justify-end text-right",
                  )}
                >
                  <span className="truncate">{column.header}</span>
                  {isSorted && (
                    <Icon
                      name={sort?.dir === "asc" ? "chevron-up" : "chevron-down"}
                      size={14}
                      className="shrink-0 text-purple-600 dark:text-purple-400"
                    />
                  )}
                </button>
              );
            })}
          </div>

          {isLoading ? (
            Array.from({ length: SKELETON_ROWS }).map((_, i) => (
              <div
                key={i}
                className="grid items-center border-b border-gray-100 dark:border-gray-800 px-4"
                style={{ gridTemplateColumns }}
              >
                {columns.map((column) => (
                  <div
                    key={column.key}
                    className="py-4 pr-2"
                  >
                    <div className="h-3.5 w-2/3 animate-pulse rounded bg-gray-100 dark:bg-gray-800" />
                  </div>
                ))}
              </div>
            ))
          ) : isEmpty && emptyState ? (
            <EmptyState
              icon={emptyState.icon}
              title={emptyState.title}
              description={emptyState.description ?? ""}
              className="!border-0 !shadow-none"
            />
          ) : (
            sortedRows.map((row) => {
              const key = rowKey(row);
              return (
                <div
                  key={key}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  className={clsx(
                    "grid items-center border-b border-gray-100 dark:border-gray-800 px-4",
                    onRowClick && "cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50",
                    selectedKey === key && "bg-purple-50/50 dark:bg-purple-900/10",
                  )}
                  style={{ gridTemplateColumns }}
                >
                  {columns.map((column) => (
                    <div
                      key={column.key}
                      className={clsx("min-w-0 py-3 pr-2 text-sm text-gray-900 dark:text-gray-100", column.align === "right" && "text-right tabular-nums")}
                    >
                      {column.render(row)}
                    </div>
                  ))}
                </div>
              );
            })
          )}
        </div>
      </div>

      {pagination && pagination.total > 0 && (
        <View className="flex flex-row items-center justify-between px-4 py-2">
          <Text className="text-xs tabular-nums text-gray-400 dark:text-gray-500">
            {formatCount((pagination.page - 1) * pagination.pageSize + 1)}–{formatCount(Math.min(pagination.page * pagination.pageSize, pagination.total))} of{" "}
            {formatCount(pagination.total)}
          </Text>
          <View className="flex flex-row items-center gap-1">
            <IconButton
              icon="chevron-left"
              variant="info"
              isDisabled={pagination.page <= 1}
              onClick={() => pagination.onPageChange(pagination.page - 1)}
              title="Previous page"
            />
            <IconButton
              icon="chevron-right"
              variant="info"
              isDisabled={pagination.page * pagination.pageSize >= pagination.total}
              onClick={() => pagination.onPageChange(pagination.page + 1)}
              title="Next page"
            />
          </View>
        </View>
      )}
    </View>
  );
}
